import {App, Button, Divider, Flex, List, Spin, Tooltip} from "antd";
import {FC, useState} from "react";
import {Event, EventApprover, EventPatch} from '../../../../types/domain.ts';
import dayjs from "dayjs";
import {isAirportsEvent, isRouteEvent} from "../../../../utils/eventGuards.ts";
import {EventEditableStatistic} from "../EventEditableStatistic/EventEditableStatistic.tsx";
import {EditableStatistic} from "../../../../components/EditableStatistic/EditableStatistic.tsx";
import {eventsEventIdPatch} from "../../../../api/events/eventsEventIdPatch.ts";
import styles from './EventInfoContent.module.css';
import {AirportListItem} from "./AirportListItem.tsx";
import {useOwnProfile} from "../../../../stores/OwnProfileStore.ts";
import {eventsEventIdVatsimPost} from "../../../../api/events/eventsEventIdVatsimPost.ts";
import {eventsEventIdRequestVatsimPost} from "../../../../api/events/eventsEventIdRequestVatsimPost.ts";
import {ExceptionOutlined, FileDoneOutlined, MailOutlined, PlusOutlined} from "@ant-design/icons";
import {AddAirportModal} from "./AddAirportModal.tsx";
import {RequestVatrusModal} from "../RequestVatrusModal/RequestVatrusModal.tsx";

type Props = {
    event: Event;
    editable: boolean;
    onUpdate: VoidFunction;
    approvers?: EventApprover[];
}

export const EventInfoContent: FC<Props> = ({ event, editable, onUpdate, approvers }) => {
    const { message } = App.useApp();
    const { profile } = useOwnProfile();

    const [addAirportOpen, setAddAirportOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const handleEdit = (name: keyof EventPatch, value: string) => {
        setLoading(true);
        eventsEventIdPatch(event.id, {
            [name]: value
        }).then(() => {
            void message.success('Сохранено');
            onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setLoading(false));
    }

    const [vatsimLoading, setVatsimLoading] = useState(false);
    const handleVatsim = () => {
        setVatsimLoading(true);
        eventsEventIdVatsimPost(event.id).then(() => {
            void message.success('Сохранено');
            onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setVatsimLoading(false));
    }

    const [requestVatsimLoading, setRequestVatsimLoading] = useState(false);
    const handleRequestVatsim = () => {
        setRequestVatsimLoading(true);
        eventsEventIdRequestVatsimPost(event.id).then(() => {
            void message.success('Сохранено');
            onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setRequestVatsimLoading(false));
    }

    const [requestVatusOpen, setRequestVatrusOpen] = useState(false);

    return (
        <>
            <Flex vertical gap={16} flex="1 0">
                {isAirportsEvent(event) && (
                    <EditableStatistic
                        title="Аэропорты"
                        value={(
                            <>
                                <List
                                    dataSource={event.airports}
                                    renderItem={(airport) => <AirportListItem airport={airport} event={event} onUpdate={onUpdate} disabled={!editable} />}
                                />
                                {editable && (
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => setAddAirportOpen(true)} block>
                                        Добавить аэропорт
                                    </Button>
                                )}
                            </>
                        )}
                    />
                )}
                {isRouteEvent(event) && (
                    <>
                        <Flex gap={4}>
                            <EditableStatistic
                                title="Аэропорты вылета"
                                value={(
                                    <List
                                        dataSource={event.departures}
                                        renderItem={(airport) => <AirportListItem airport={airport} event={event} onUpdate={onUpdate} disabled={!editable} />}
                                    />
                                )}
                            />
                            <EditableStatistic
                                title="Аэропорты прибытия"
                                value={(
                                    <List
                                        dataSource={event.arrivals}
                                        renderItem={(airport) => <AirportListItem airport={airport} event={event} onUpdate={onUpdate} disabled={!editable} />}
                                    />
                                )}
                            />
                        </Flex>
                        {editable && (
                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => setAddAirportOpen(true)} block>
                                Добавить аэропорт
                            </Button>
                        )}
                    </>
                )}
                <Divider className={styles.divider} />
                <EditableStatistic
                    title="Публикация на VATSIM"
                    value={event.vatsim_published ? 'Отправлено' : (
                        <Flex gap={8}>
                            <Button icon={<FileDoneOutlined />} size="small" loading={vatsimLoading} onClick={handleVatsim}>Отправлено</Button>
                            {(profile?.role === 'staff' || profile?.role === 'region_manager') && (
                                <Button icon={<ExceptionOutlined />} size="small" loading={requestVatsimLoading} onClick={handleRequestVatsim}>Запросить подтверждение участников</Button>
                            )}
                        </Flex>
                    )}
                />
                {event.created_by.id === profile?.id && (
                    <EditableStatistic
                        title="Публикация на VATRUS"
                        value={approvers?.every(({ status }) => status === 'approved') ? (
                            <Button
                                icon={<MailOutlined />}
                                size="small"
                                onClick={() => setRequestVatrusOpen(true)}
                            >Запросить</Button>
                        ) : (
                            <Tooltip title="Будет доступно после согласования ивента">
                                <Button
                                    icon={<MailOutlined />}
                                    size="small"
                                    disabled
                                >Запросить</Button>
                            </Tooltip>
                        )}
                    />
                )}
                <EditableStatistic
                    title="Тип"
                    value={isAirportsEvent(event) ? `Стаффинг аэропортов: ${event.category.name}` : 'Полет по маршруту'}
                />
                <EventEditableStatistic
                    title="Предварительная дата"
                    name="date"
                    defaultValue={dayjs.utc(event.date).format('YYYY-MM-DD')}
                    onChange={handleEdit}
                    editable={editable}
                />
                {isRouteEvent(event) && (
                    <EventEditableStatistic
                        title="Маршрут"
                        name="route"
                        defaultValue={event.route || '-'}
                        onChange={handleEdit}
                        editable={editable}
                    />
                )}
                <EventEditableStatistic
                    title="Описание 🇷🇺"
                    name="description"
                    defaultValue={event.description || '-'}
                    onChange={handleEdit}
                    editable={editable}
                    preLine
                />
                <EventEditableStatistic
                    title="Описание 🇬🇧"
                    name="description_en"
                    defaultValue={event.description_en || '-'}
                    onChange={handleEdit}
                    editable={editable}
                    preLine
                />
            </Flex>
            <Spin fullscreen spinning={loading} />
            <AddAirportModal event={event} open={addAirportOpen} onClose={() => setAddAirportOpen(false)} onSuccess={onUpdate} />
            <RequestVatrusModal open={requestVatusOpen} onClose={() => setRequestVatrusOpen(false)} eventId={event.id} />
        </>
    )
}